import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useNavigate } from 'react-router-dom';
import { COUNTRIES, getRegionsForCountry } from '../../../shared/constants/locations';

interface SubcontractorMatchmakingProps {
  enablePartnerSearch: boolean;
  setEnablePartnerSearch: (val: boolean) => void;
  matchmakingCountryFilter: string;
  setMatchmakingCountryFilter: (val: string) => void;
  matchmakingRegionFilter: string;
  setMatchmakingRegionFilter: (val: string) => void;
  matchmakingMinScore: number;
  setMatchmakingMinScore: (val: number) => void;
  enablePartnerVisibility: boolean;
  setEnablePartnerVisibility: (val: boolean) => void;
}

export const SubcontractorMatchmaking: React.FC<SubcontractorMatchmakingProps> = ({
  enablePartnerSearch,
  setEnablePartnerSearch,
  matchmakingCountryFilter,
  setMatchmakingCountryFilter,
  matchmakingRegionFilter,
  setMatchmakingRegionFilter,
  matchmakingMinScore,
  setMatchmakingMinScore,
  enablePartnerVisibility,
  setEnablePartnerVisibility,
}) => {
  const navigate = useNavigate();

  return (
    <>
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
        Subcontractor Matchmaking
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <FormGroup>
        <FormControlLabel
          control={
            <Switch
              checked={enablePartnerSearch}
              onChange={(e) => setEnablePartnerSearch(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body1">Enable Partner Search</Typography>
              <Typography variant="body2" color="text.secondary">
                Allow your Capability Analyses to search the global database and recommend partners
                for capability gaps.
              </Typography>
            </Box>
          }
          sx={{ mb: 2, alignItems: 'flex-start' }}
        />

        {enablePartnerSearch && (
          <Box sx={{ ml: 4, mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              select
              disabled
              label="Country Restrict"
              variant="outlined"
              size="small"
              value={matchmakingCountryFilter || 'Australia'}
              onChange={(e) => {
                setMatchmakingCountryFilter(e.target.value);
                setMatchmakingRegionFilter('');
              }}
              FormHelperTextProps={{ sx: { fontSize: '0.875rem' } }}
              helperText="Currently limited to Australia."
            >
              <MenuItem value="">
                <em>Global (Any Country)</em>
              </MenuItem>
              {COUNTRIES.map((country) => (
                <MenuItem key={country.code} value={country.name}>
                  {country.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Region / State Restrict"
              variant="outlined"
              size="small"
              value={matchmakingRegionFilter}
              onChange={(e) => setMatchmakingRegionFilter(e.target.value)}
              disabled={!matchmakingCountryFilter}
              FormHelperTextProps={{ sx: { fontSize: '0.875rem' } }}
              helperText="Only match partners from this region/state. Leave blank for all regions."
            >
              <MenuItem value="">
                <em>All Regions</em>
              </MenuItem>
              {getRegionsForCountry(matchmakingCountryFilter || 'Australia').map((region) => (
                <MenuItem key={region} value={region}>
                  {region}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Minimum Match Score (%)"
              variant="outlined"
              size="small"
              type="number"
              inputProps={{ min: 0, max: 100, step: 10 }}
              value={Math.round(matchmakingMinScore * 100)}
              onChange={(e) => setMatchmakingMinScore((parseFloat(e.target.value) || 0) / 100)}
              FormHelperTextProps={{ sx: { fontSize: '0.875rem' } }}
              helperText="Percentage (e.g., 70 = 70% minimum match threshold)"
            />
          </Box>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={enablePartnerVisibility}
              onChange={(e) => setEnablePartnerVisibility(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body1">Enable Partner Visibility</Typography>
              <Typography variant="body2" color="text.secondary">
                Allow your registered partner profile to be discovered by other companies' searches.
              </Typography>
            </Box>
          }
          sx={{ mb: 2, alignItems: 'flex-start' }}
        />
        <Box sx={{ ml: 4, mb: 2 }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/partner-registration')}
            sx={{
              backgroundColor: 'black',
              color: 'white',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#333',
              },
            }}
          >
            Manage Partner Profile
          </Button>
        </Box>
      </FormGroup>
    </>
  );
};
